import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { TranslationService } from '../../../services/translation.service';
import { ClusterVM, ClusterSymptomVM } from './symptoms-vm';

/**
 * "How your symptoms connect" — interactive cluster map. Tabs = clusters that
 * emerged from the user's report; chips = the user's reported symptoms in the
 * active cluster; tapping a chip opens that symptom's detail (real rating +
 * real co-occurrence + hormone education). All data is real scan data; only the
 * educational prose is fixed. Behaviour ports the v11 reference map.
 */
@Component({
  selector: 'app-symptom-cluster-map',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="card" *ngIf="clusters?.length" style="padding:16px 12px 14px;margin-top:10px;">
      <h3 class="section-title" style="margin-bottom:4px;">{{ 'results.sym.mapTitle' | translate }}</h3>
      <p style="font-size:12px;color:var(--g500);line-height:1.5;margin-bottom:14px;">{{ 'results.sym.mapSub1' | translate }}</p>
      <p style="font-size:12px;color:var(--g500);line-height:1.6;margin-bottom:14px;">{{ 'results.sym.mapSub2' | translate }}</p>

      <!-- Cluster tabs -->
      <div style="display:flex;gap:8px;margin-bottom:14px;">
        <button
          *ngFor="let c of clusters"
          type="button"
          (click)="pickCluster(c)"
          [style.flex]="'1'"
          [style.padding]="'9px 6px'"
          [style.border-radius]="'999px'"
          [style.border]="'1.5px solid ' + (c.id === activeId ? c.color : c.color + '60')"
          [style.background]="c.id === activeId ? c.color : 'transparent'"
          [style.color]="c.id === activeId ? '#fff' : c.color"
          [style.opacity]="c.id === activeId ? '1' : '0.6'"
          style="cursor:pointer;font-size:11px;font-weight:700;font-family:inherit;text-align:center;line-height:1.3;transition:all 0.2s ease;"
        >
          {{ c.labelKey | translate }}
        </button>
      </div>

      <!-- Symptom chips (active cluster) -->
      <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px;">
        <button
          *ngFor="let s of active?.symptoms"
          type="button"
          (click)="pickSymptom(s)"
          [style.background]="s.id === activeSymId ? active!.color : lightBg"
          [style.color]="s.id === activeSymId ? '#fff' : active!.color"
          [style.border]="'1.5px solid ' + (s.id === activeSymId ? active!.color : active!.color + '45')"
          [style.font-weight]="s.id === activeSymId ? '700' : '500'"
          [style.opacity]="activeSymId && s.id !== activeSymId ? '0.55' : '1'"
          style="display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:999px;font-size:13px;font-family:inherit;cursor:pointer;transition:all 0.18s ease;"
        >
          {{ s.labelKey | translate }}
        </button>
      </div>

      <!-- Insight card -->
      <div style="padding:14px 16px;background:#faf9ff;border-radius:var(--r-sm);border:1px solid #eae5fa;min-height:64px;">
        <ng-container *ngIf="!activeSym && active">
          <div style="margin-bottom:8px;">
            <span [style.background]="active.color" style="font-size:10px;font-weight:700;color:#fff;border-radius:999px;padding:2px 9px;letter-spacing:0.02em;">{{ active.badgeKey | translate }}</span>
          </div>
          <div style="font-size:14px;font-weight:700;color:var(--navy);margin-bottom:6px;">{{ active.insightTitleKey | translate }}</div>
          <div style="font-size:13px;line-height:1.65;color:var(--g600);margin-bottom:12px;">{{ active.insightBodyKey | translate }}</div>
        </ng-container>

        <ng-container *ngIf="activeSym && active">
          <div [style.color]="active.color" style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.07em;margin-bottom:5px;">{{ 'results.sym.mapCluster' | translate }} {{ active.labelKey | translate }}</div>
          <div style="font-size:18px;font-weight:800;color:var(--navy);margin-bottom:8px;">{{ activeSym.labelKey | translate }}</div>
          <div [innerHTML]="(activeSym.bodyKey || activeSym.descKey) | translate" style="font-size:13px;line-height:1.68;color:var(--g600);margin-bottom:10px;"></div>

          <!-- real rating -->
          <div style="margin-bottom:10px;">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
              <span style="font-size:10px;font-weight:700;color:var(--g400);text-transform:uppercase;letter-spacing:0.06em;">{{ 'results.sym.mapYourRating' | translate }}</span>
              <span dir="ltr" style="font-size:11px;font-weight:700;color:var(--navy);">{{ activeSym.rating }} / 10</span>
            </div>
            <div style="height:4px;background:#f0edf9;border-radius:999px;">
              <div [style.width.%]="activeSym.rating * 10" [style.background]="activeSym.color" style="height:4px;border-radius:999px;"></div>
            </div>
          </div>

          <!-- co-occurring symptoms (real) -->
          <ng-container *ngIf="activeSym.alongside.length">
            <div style="font-size:10px;font-weight:700;color:var(--g400);text-transform:uppercase;letter-spacing:0.07em;margin-bottom:6px;">{{ 'results.sym.mapAlongside' | translate }}</div>
            <div style="display:flex;flex-wrap:wrap;gap:5px;margin-bottom:12px;">
              <span
                *ngFor="let a of activeSym.alongside"
                [style.color]="active.color"
                [style.border]="'1px solid ' + active.color + '40'"
                style="font-size:11px;font-weight:500;background:#fff;border-radius:999px;padding:3px 9px;white-space:nowrap;"
              >{{ a | translate }}</span>
            </div>
          </ng-container>
        </ng-container>

        <!-- tools that can help (real cluster tools) -->
        <div *ngIf="active?.toolNames?.length" style="margin-top:10px;border-top:1px solid var(--g100);padding-top:10px;font-size:12px;color:var(--g500);line-height:1.6;">
          {{ 'results.sym.mapToolsLabel' | translate }}
          <span style="color:var(--navy);font-weight:600;">{{ toolList }}</span>
        </div>
        <div style="margin-top:10px;font-size:10px;line-height:1.5;color:var(--g400);">{{ 'results.sym.mapDisclaimer' | translate }}</div>
      </div>
    </div>
  `,
})
export class SymptomClusterMapComponent implements OnChanges {
  @Input() clusters: ClusterVM[] = [];

  active: ClusterVM | null = null;
  activeSym: ClusterSymptomVM | null = null;

  constructor(private translation: TranslationService) {}

  ngOnChanges(): void {
    this.active = this.clusters?.[0] ?? null;
    this.activeSym = null;
  }

  get activeId(): string | null {
    return this.active?.id ?? null;
  }
  get activeSymId(): string | null {
    return this.activeSym?.id ?? null;
  }
  get lightBg(): string {
    return (this.active?.color ?? '#888') + '14';
  }
  /** translated, comma-joined tool names for the active cluster */
  get toolList(): string {
    return (this.active?.toolNames ?? []).map((k) => this.translation.translate(k)).join(', ');
  }

  pickCluster(c: ClusterVM): void {
    this.active = c;
    this.activeSym = null;
  }
  pickSymptom(s: ClusterSymptomVM): void {
    this.activeSym = this.activeSym?.id === s.id ? null : s;
  }
}
